import { api } from "@/lib/api";
import type { EmailDraft, InboxDirectory } from "@/services/inbound-email.service";

export type TaskPriority = "low" | "medium" | "high" | "urgent";
export type GroupingMode = "person_topic" | "per_email";
export type NestedListMode = "checklist" | "subtasks";
export type ReviewMode = "always";

export interface EmailToTaskSettings {
	id: string;
	enabled: boolean;
	inbound_address: string;
	webhook_url: string;
	hub_base_url: string | null;
	hub_agent_id: string | null;
	hub_agent_name: string | null;
	extra_instructions: string | null;
	request_timeout_sec: number;
	allowed_sender_domains: string[];
	allowed_sender_emails: string[];
	require_taskhub_user: boolean;
	require_dkim_pass: boolean;
	default_project_id: string | null;
	default_priority: TaskPriority;
	assign_active_sprint: boolean;
	grouping_mode: GroupingMode;
	nested_list_mode: NestedListMode;
	resolve_due_dates: boolean;
	review_mode: ReviewMode;
	notify_sender_on_ready: boolean;
	notify_sender_on_approved: boolean;
	notify_reviewers_in_app: boolean;
	max_attempts: number;
	last_webhook_at: string | null;
	last_processed_at: string | null;
	last_error: string | null;
	resend_webhook_secret_set: boolean;
	resend_webhook_secret_hint: string | null;
	resend_api_key_set: boolean;
	resend_api_key_hint: string | null;
	hub_api_key_set: boolean;
	hub_api_key_hint: string | null;
	updated_at: string;
}

export type SecretField = "resend_webhook_secret" | "resend_api_key" | "hub_api_key";

/** Editable settings fields (everything the PATCH accepts except secrets). */
export type EmailToTaskEditable = Pick<
	EmailToTaskSettings,
	| "enabled"
	| "inbound_address"
	| "hub_base_url"
	| "hub_agent_id"
	| "hub_agent_name"
	| "extra_instructions"
	| "request_timeout_sec"
	| "allowed_sender_domains"
	| "allowed_sender_emails"
	| "require_taskhub_user"
	| "require_dkim_pass"
	| "default_project_id"
	| "default_priority"
	| "assign_active_sprint"
	| "grouping_mode"
	| "nested_list_mode"
	| "resolve_due_dates"
	| "review_mode"
	| "notify_sender_on_ready"
	| "notify_sender_on_approved"
	| "notify_reviewers_in_app"
	| "max_attempts"
>;

/** String sets a secret, null clears it, absent keeps it. */
export type EmailToTaskPatch = Partial<EmailToTaskEditable> &
	Partial<Record<SecretField, string | null>>;

export interface HubAgent {
	id: string;
	name: string;
	description: string | null;
	enabled: boolean;
}

export async function getEmailToTaskSettings(): Promise<EmailToTaskSettings> {
	const res = await api.get<{ success: boolean; data: { settings: EmailToTaskSettings } }>(
		"/email-to-task/settings",
	);
	return res.data.settings;
}

export async function updateEmailToTaskSettings(
	payload: EmailToTaskPatch,
): Promise<EmailToTaskSettings> {
	const res = await api.patch<{ success: boolean; data: { settings: EmailToTaskSettings } }>(
		"/email-to-task/settings",
		payload,
	);
	return res.data.settings;
}

/** Tests the Hub connection; unsaved URL/key override the saved ones. */
export async function testHubConnection(payload: {
	hub_base_url?: string | null;
	hub_api_key?: string;
}): Promise<{ message: string; agents: HubAgent[] }> {
	const res = await api.post<{ success: boolean; message: string; data: { agents: HubAgent[] } }>(
		"/email-to-task/hub/test",
		payload,
	);
	return { message: res.message, agents: res.data.agents };
}

export async function listHubAgents(): Promise<{ agents: HubAgent[]; configured: boolean }> {
	const res = await api.get<{ success: boolean; data: { agents: HubAgent[]; configured: boolean } }>(
		"/email-to-task/hub/agents",
	);
	return res.data;
}

/** Runs the saved agent on pasted text. Nothing is stored. */
export async function testRunExtraction(payload: {
	text: string;
	subject?: string;
}): Promise<{ draft: EmailDraft; directory: InboxDirectory }> {
	const res = await api.post<{ success: boolean; data: { draft: EmailDraft; directory: InboxDirectory } }>(
		"/email-to-task/test-run",
		payload,
	);
	return res.data;
}
